import { Codicon } from 'vscode/vscode/vs/base/common/codicons';
import { ThemeIcon } from 'vscode/vscode/vs/base/common/themables';
import { localize2WithPath, localizeWithPath } from 'vscode/vscode/vs/nls';
import { IAccessibilityService } from 'vscode/vscode/vs/platform/accessibility/common/accessibility.service';
import { Action2 } from 'vscode/vscode/vs/platform/actions/common/actions';
import { AccessibilitySignal, AcknowledgeDocCommentsToken } from 'vscode/vscode/vs/platform/accessibilitySignal/browser/accessibilitySignalService';
import { IAccessibilitySignalService } from 'vscode/vscode/vs/platform/accessibilitySignal/browser/accessibilitySignalService.service';
import { IConfigurationService } from 'vscode/vscode/vs/platform/configuration/common/configuration.service';
import { IQuickInputService } from 'vscode/vscode/vs/platform/quickinput/common/quickInput.service';
import { IPreferencesService } from 'vscode/vscode/vs/workbench/services/preferences/common/preferences.service';

const _moduleId = "vs/workbench/contrib/accessibilitySignals/browser/commands";
class ShowSignalSoundHelp extends Action2 {
    static { this.ID = 'signals.sounds.help'; }
    constructor() {
        super({
            id: ShowSignalSoundHelp.ID,
            title: ( localize2WithPath(_moduleId, 0, "Help: List Signal Sounds")),
            f1: true,
            metadata: {
                description: ( localizeWithPath(
                    _moduleId,
                    1,
                    "List all accessibility sounds, noises, or audio cues and configure their settings"
                ))
            }
        });
    }
    async run(accessor) {
        const accessibilitySignalService = accessor.get(IAccessibilitySignalService);
        const quickInputService = accessor.get(IQuickInputService);
        const configurationService = accessor.get(IConfigurationService);
        const accessibilityService = accessor.get(IAccessibilityService);
        const preferencesService = accessor.get(IPreferencesService);
        const userGestureSignals = [AccessibilitySignal.save, AccessibilitySignal.format];
        const items = ( (AccessibilitySignal.allAccessibilitySignals.map((signal, idx) => ({
            label: userGestureSignals.includes(signal) ? `${signal.name} (${configurationService.getValue(signal.settingsKey + '.sound')})` : signal.name,
            signal,
            buttons: userGestureSignals.includes(signal) ? [{
                    iconClass: ThemeIcon.asClassName(Codicon.settingsGear),
                    tooltip: ( localizeWithPath(_moduleId, 2, 'Configure Sound')),
                    alwaysVisible: true
                }] : []
        })))).sort((a, b) => a.label.localeCompare(b.label));
        const qp = quickInputService.createQuickPick();
        qp.items = items;
        qp.selectedItems = items.filter(i => accessibilitySignalService.isSoundEnabled(i.signal) || userGestureSignals.includes(i.signal) && configurationService.getValue(i.signal.settingsKey + '.sound') !== 'never');
        qp.onDidAccept(() => {
            const enabledSounds = ( (qp.selectedItems.map(i => i.signal)));
            const disabledSounds = ( (qp.items.map(i => i.signal))).filter(i => !enabledSounds.includes(i));
            for (const signal of enabledSounds) {
                let { sound, announcement } = configurationService.getValue(signal.settingsKey);
                sound = userGestureSignals.includes(signal) ? 'userGesture' : accessibilityService.isScreenReaderOptimized() ? 'auto' : 'on';
                if (announcement) {
                    configurationService.updateValue(signal.settingsKey, { sound, announcement });
                }
                else {
                    configurationService.updateValue(signal.settingsKey, { sound });
                }
            }
            for (const signal of disabledSounds) {
                const announcement = configurationService.getValue(signal.settingsKey + '.announcement');
                const sound = getDisabledSettingValue(userGestureSignals.includes(signal), accessibilityService.isScreenReaderOptimized());
                const value = announcement ? { sound, announcement } : { sound };
                configurationService.updateValue(signal.settingsKey, value);
            }
            qp.hide();
        });
        qp.onDidTriggerItemButton(e => {
            preferencesService.openUserSettings({ jsonEditor: true, revealSetting: { key: e.item.signal.settingsKey, edit: true } });
        });
        qp.onDidChangeActive(() => {
            accessibilitySignalService.playSound(qp.activeItems[0].signal.sound.getSound(true), true, AcknowledgeDocCommentsToken);
        });
        qp.placeholder = ( localizeWithPath(_moduleId, 3, 'Select a sound to play and configure'));
        qp.canSelectMany = true;
        await qp.show();
    }
}
function getDisabledSettingValue(isUserGestureSignal, isScreenReaderOptimized) {
    return isScreenReaderOptimized ? (isUserGestureSignal ? 'never' : 'off') : (isUserGestureSignal ? 'never' : 'auto');
}
class ShowAccessibilityAnnouncementHelp extends Action2 {
    static { this.ID = 'accessibility.announcement.help'; }
    constructor() {
        super({
            id: ShowAccessibilityAnnouncementHelp.ID,
            title: ( localize2WithPath(_moduleId, 4, "Help: List Signal Announcements")),
            f1: true,
            metadata: {
                description: ( localizeWithPath(
                    _moduleId,
                    5,
                    "List all accessibility announcements, alerts, braille messages, and configure their settings"
                ))
            }
        });
    }
    async run(accessor) {
        const accessibilitySignalService = accessor.get(IAccessibilitySignalService);
        const quickInputService = accessor.get(IQuickInputService);
        const configurationService = accessor.get(IConfigurationService);
        const accessibilityService = accessor.get(IAccessibilityService);
        const preferencesService = accessor.get(IPreferencesService);
        const userGestureSignals = [AccessibilitySignal.save, AccessibilitySignal.format];
        const items = ( (AccessibilitySignal.allAccessibilitySignals.filter(c => !!c.legacyAnnouncementSettingsKey).map((signal, idx) => ({
            label: userGestureSignals.includes(signal) ? `${signal.name} (${configurationService.getValue(signal.settingsKey + '.announcement')})` : signal.name,
            signal,
            buttons: userGestureSignals.includes(signal) ? [{
                    iconClass: ThemeIcon.asClassName(Codicon.settingsGear),
                    tooltip: ( localizeWithPath(_moduleId, 6, 'Configure Announcement')),
                    alwaysVisible: true,
                }] : []
        })))).sort((a, b) => a.label.localeCompare(b.label));
        const qp = quickInputService.createQuickPick();
        qp.items = items;
        qp.selectedItems = items.filter(i => accessibilitySignalService.isAnnouncementEnabled(i.signal) || userGestureSignals.includes(i.signal) && configurationService.getValue(i.signal.settingsKey + '.announcement') !== 'never');
        const screenReaderOptimized = accessibilityService.isScreenReaderOptimized();
        qp.onDidAccept(() => {
            if (!screenReaderOptimized) {
                qp.hide();
                return;
            }
            const enabledAnnouncements = ( (qp.selectedItems.map(i => i.signal)));
            const disabledAnnouncements = AccessibilitySignal.allAccessibilitySignals.filter(cue => !!cue.legacyAnnouncementSettingsKey && !enabledAnnouncements.includes(cue));
            for (const signal of enabledAnnouncements) {
                let { sound, announcement } = configurationService.getValue(signal.settingsKey);
                announcement = userGestureSignals.includes(signal) ? 'userGesture' : signal.announcementMessage && accessibilityService.isScreenReaderOptimized() ? 'auto' : undefined;
                configurationService.updateValue(signal.settingsKey, { sound, announcement });
            }
            for (const signal of disabledAnnouncements) {
                const announcement = getDisabledSettingValue(userGestureSignals.includes(signal), true);
                const sound = configurationService.getValue(signal.settingsKey + '.sound');
                const value = announcement ? { sound, announcement } : { sound };
                configurationService.updateValue(signal.settingsKey, value);
            }
            qp.hide();
        });
        qp.onDidTriggerItemButton(e => {
            preferencesService.openUserSettings({ jsonEditor: true, revealSetting: { key: e.item.signal.settingsKey, edit: true } });
        });
        qp.placeholder = screenReaderOptimized ? ( localizeWithPath(_moduleId, 7, 'Select an announcement to configure')) : ( localizeWithPath(
            _moduleId,
            8,
            'Screen reader is not active, announcements are disabled by default.'
        ));
        qp.canSelectMany = true;
        await qp.show();
    }
}

export { ShowAccessibilityAnnouncementHelp, ShowSignalSoundHelp };
